import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Edit, Search, Filter, Pencil } from "lucide-react";
import { useProductsFiscal, FiscalBatchData, ProductFiscal } from "@/hooks/useProductsFiscal";
import { useTaxRules } from "@/hooks/useTaxRules";
import { useCategories } from "@/hooks/useCategories";
import { ProductFiscalBatchDialog } from "./ProductFiscalBatchDialog";
import { ProductFiscalEditDialog } from "./ProductFiscalEditDialog";

export function ProductFiscalList() {
  const { products, loading, updateProductsFiscalBatch, updateProductFiscal } = useProductsFiscal();
  const { taxRules } = useTaxRules();
  const { categories } = useCategories();
  
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [fiscalFilter, setFiscalFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<ProductFiscal | null>(null);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      // Busca
      if (search && !product.name.toLowerCase().includes(search.toLowerCase())) {
        return false;
      }
      // Categoria
      if (categoryFilter !== "all" && product.category_id !== categoryFilter) {
        return false;
      }
      // Filtro fiscal
      if (fiscalFilter === "sem_ncm" && product.ncm) {
        return false;
      }
      if (fiscalFilter === "com_ncm" && !product.ncm) {
        return false;
      }
      if (fiscalFilter === "sem_regra" && product.tax_rule_id) {
        return false;
      }
      if (fiscalFilter === "com_regra" && !product.tax_rule_id) {
        return false;
      }
      return true;
    });
  }, [products, search, categoryFilter, fiscalFilter]);

  const toggleSelection = (id: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedIds(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredProducts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredProducts.map((p) => p.id)));
    }
  };

  const handleBatchSave = async (data: FiscalBatchData) => {
    const success = await updateProductsFiscalBatch(Array.from(selectedIds), data);
    if (success) {
      setSelectedIds(new Set());
    }
    return success;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-10 bg-muted rounded w-1/3" />
            <div className="h-20 bg-muted rounded" />
            <div className="h-20 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Dados Fiscais dos Produtos</CardTitle>
          <CardDescription>
            Configure NCM, CFOP e demais dados fiscais. Selecione produtos para editar em lote.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filtros */}
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar produto..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Categorias</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={fiscalFilter} onValueChange={setFiscalFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filtro Fiscal" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="sem_ncm">Sem NCM</SelectItem>
                <SelectItem value="com_ncm">Com NCM</SelectItem>
                <SelectItem value="sem_regra">Sem Regra</SelectItem>
                <SelectItem value="com_regra">Com Regra</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="default"
              disabled={selectedIds.size === 0}
              onClick={() => setBatchDialogOpen(true)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Editar Lote ({selectedIds.size})
            </Button>
          </div>

          {/* Tabela */}
          <div className="overflow-x-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={selectedIds.size === filteredProducts.length && filteredProducts.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>NCM</TableHead>
                  <TableHead>CFOP</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>Unidade</TableHead>
                  <TableHead>Regra Tributária</TableHead>
                  <TableHead className="w-[70px]">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      Nenhum produto encontrado.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProducts.map((product) => (
                    <TableRow key={product.id} className={selectedIds.has(product.id) ? "bg-muted/50" : ""}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(product.id)}
                          onCheckedChange={() => toggleSelection(product.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{product.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {product.category?.name || "-"}
                      </TableCell>
                      <TableCell>
                        {product.ncm ? (
                          <Badge variant="outline">{product.ncm}</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-yellow-600">Não definido</Badge>
                        )}
                      </TableCell>
                      <TableCell>{product.cfop || "-"}</TableCell>
                      <TableCell>{product.origem || "-"}</TableCell>
                      <TableCell>{product.unidade_comercial || "-"}</TableCell>
                      <TableCell>
                        {product.tax_rule?.name ? (
                          <Badge variant="default" className="bg-primary/80">
                            {product.tax_rule.name}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditProduct(product)}
                          title="Editar dados fiscais"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Resumo */}
          <div className="flex justify-between items-center text-sm text-muted-foreground">
            <span>
              {filteredProducts.length} produto(s) • {selectedIds.size} selecionado(s)
            </span>
            {selectedIds.size > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                Limpar seleção
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <ProductFiscalBatchDialog
        open={batchDialogOpen}
        onOpenChange={setBatchDialogOpen}
        selectedCount={selectedIds.size}
        taxRules={taxRules}
        onSave={handleBatchSave}
      />

      <ProductFiscalEditDialog
        open={!!editProduct}
        onOpenChange={(open) => !open && setEditProduct(null)}
        product={editProduct}
        taxRules={taxRules}
        onSave={updateProductFiscal}
      />
    </>
  );
}
